import { useState, useEffect } from "react";
import { Plus, Search, Edit, Trash2, X, User, Phone, Mail, Calendar } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const roleLabels = { owner: "Owner", manager: "Manager", hr_finance: "HR & Finance", sales_team: "Sales Team", supervisor: "Supervisor", employee: "Employee" };
const deptColors = { Management: "bg-purple-100 text-purple-700", Production: "bg-green-100 text-green-700", "Quality Control": "bg-blue-100 text-blue-700", "HR & Finance": "bg-amber-100 text-amber-700", Sales: "bg-pink-100 text-pink-700", Packaging: "bg-teal-100 text-teal-700" };

const emptyForm = { name: "", role: "employee", department: "Production", phone: "", email: "", join_date: new Date().toISOString().split("T")[0], salary_type: "monthly_fixed", pay_rate: 0 };

export default function EmployeeManagement() {
  const { api, user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const canEdit = ["owner", "manager", "hr_finance"].includes(user?.role);

  const load = () => { api().get("/employees").then(r => setEmployees(r.data)).catch(() => toast.error("Failed to load employees")); };
  useEffect(load, [api]);

  const filtered = employees.filter(e => {
    const matchSearch = e.name.toLowerCase().includes(search.toLowerCase()) || e.email.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" || e.department === filter;
    return matchSearch && matchFilter;
  });

  const departments = [...new Set(employees.map(e => e.department))];

  const openAdd = () => { setEditId(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (emp) => { setEditId(emp.id); setForm({ name: emp.name, role: emp.role, department: emp.department, phone: emp.phone, email: emp.email, join_date: emp.join_date, salary_type: emp.salary_type || "monthly_fixed", pay_rate: emp.pay_rate || 0 }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editId) {
        await api().put(`/employees/${editId}`, form);
        toast.success("Employee updated!");
      } else {
        await api().post("/employees", form);
        toast.success("Employee added!");
      }
      setShowModal(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try { await api().delete(`/employees/${id}`); toast.success("Employee deleted"); load(); } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-6" data-testid="employee-management">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-serif text-navy">Employee Management</h2>
          <p className="text-slate-400 text-sm font-sans mt-1">{employees.length} employees</p>
        </div>
        {canEdit && (
          <button onClick={openAdd} className="btn-primary flex items-center gap-2" data-testid="add-employee-btn">
            <Plus className="w-5 h-5" />Add Employee
          </button>
        )}
      </div>

      <div className="card-dashboard">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employees..." className="form-input pl-9" data-testid="employee-search" />
          </div>
          <select value={filter} onChange={e => setFilter(e.target.value)} className="form-input w-auto" data-testid="employee-filter">
            <option value="all">All Departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="employee-table">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Employee</th>
                <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Role/Dept</th>
                <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Salary Type</th>
                <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Pay Rate</th>
                <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Contact</th>
                {canEdit && <th className="p-3 text-xs font-medium text-slate-400 uppercase tracking-wider font-sans">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(emp => (
                <tr key={emp.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors" data-testid={`employee-row-${emp.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm font-bold">{emp.name.split(" ").map(n => n[0]).join("").slice(0, 2)}</span>
                      </div>
                      <div>
                        <p className="font-medium text-navy text-sm font-sans">{emp.name}</p>
                        <p className="text-xs text-slate-400 font-sans">{emp.join_date}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 text-xs font-medium font-sans ${deptColors[emp.department] || "bg-slate-100 text-slate-700"}`}>{emp.department}</span>
                    <p className="text-xs text-slate-400 mt-1 font-sans capitalize">{roleLabels[emp.role] || emp.role}</p>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 text-xs font-medium font-sans ${emp.salary_type === "daily_wage" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>
                      {emp.salary_type === "daily_wage" ? "Daily Wage" : "Monthly Fixed"}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className="font-mono text-sm text-navy">Rs.{(emp.pay_rate || 0).toLocaleString()}</span>
                    <span className="text-xs text-slate-400 font-sans">/{emp.salary_type === "daily_wage" ? "day" : "month"}</span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2 text-slate-500">
                      <Phone className="w-3.5 h-3.5" /><span className="text-xs font-sans">{emp.phone}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-500 mt-1">
                      <Mail className="w-3.5 h-3.5" /><span className="text-xs font-sans">{emp.email}</span>
                    </div>
                  </td>
                  {canEdit && (
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEdit(emp)} className="p-2 hover:bg-blue-50 rounded-lg transition-colors" data-testid={`edit-emp-${emp.id}`}><Edit className="w-4 h-4 text-blue-500" /></button>
                        <button onClick={() => handleDelete(emp.id)} className="p-2 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-emp-${emp.id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-center py-8 text-slate-400 font-sans">No employees found.</p>}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="employee-modal">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b"><h3 className="text-xl font-serif text-navy">{editId ? "Edit" : "Add"} Employee</h3><button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button></div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="employee-form">
              <div><label className="form-label">Full Name *</label><input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="form-input" required data-testid="emp-name-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="form-label">Role *</label><select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} className="form-input" data-testid="emp-role-select">{Object.entries(roleLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
                <div><label className="form-label">Department *</label><select value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} className="form-input" data-testid="emp-dept-select"><option>Management</option><option>Production</option><option>Quality Control</option><option>HR & Finance</option><option>Sales</option><option>Packaging</option></select></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="form-label">Phone *</label><input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="form-input" required data-testid="emp-phone-input" /></div>
                <div><label className="form-label">Email *</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="form-input" required data-testid="emp-email-input" /></div>
              </div>
              <div><label className="form-label">Joining Date *</label><input type="date" value={form.join_date} onChange={e => setForm(f => ({ ...f, join_date: e.target.value }))} className="form-input" required data-testid="emp-joindate-input" /></div>
              <div className="border-t border-slate-100 pt-4 mt-4">
                <h4 className="text-sm font-medium text-navy mb-3 font-sans">Salary Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Salary Type *</label>
                    <div className="space-y-2 mt-1">
                      {[{ val: "daily_wage", label: "Daily Wage" }, { val: "monthly_fixed", label: "Monthly Fixed" }].map(opt => (
                        <label key={opt.val} className={`flex items-center gap-3 p-3 border cursor-pointer transition-all font-sans text-sm ${form.salary_type === opt.val ? "border-forest bg-forest/5" : "border-slate-200 hover:border-forest/50"}`}>
                          <input type="radio" name="salary_type" value={opt.val} checked={form.salary_type === opt.val} onChange={e => setForm(f => ({ ...f, salary_type: e.target.value }))} className="w-4 h-4 text-forest" data-testid={`salary-type-${opt.val}`} />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="form-label">Pay Rate (Rs.) *</label>
                    <input type="number" min="0" value={form.pay_rate} onChange={e => setForm(f => ({ ...f, pay_rate: parseFloat(e.target.value) || 0 }))} className="form-input" required data-testid="emp-payrate-input" />
                    <p className="text-xs text-slate-400 mt-1 font-sans">Per {form.salary_type === "daily_wage" ? "day" : "month"}</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 border-2 border-slate-200 text-slate-600 font-medium font-sans text-sm hover:bg-slate-50 transition-colors">Cancel</button>
                <button type="submit" disabled={loading} className="flex-1 btn-primary py-2.5 text-sm" data-testid="save-employee-btn">{loading ? "Saving..." : editId ? "Update" : "Add Employee"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
